import React from 'react'
import { Input, Switch } from '../ui'
import Button from '../ui/Button/Button'
import type {
  TipoUnidadEmpaque,
  UnidadEmpaqueItem,
  UnidadesEmpaqueRecibidas
} from '../../types/devoluciones'
import {
  buildEmpaqueVerificationRows,
  getEmpaqueLabel
} from '../../utils/devoluciones-utils'

export type UnidadesEmpaqueEnabled = Record<TipoUnidadEmpaque, boolean>

interface DevolucionEmpaqueTableProps {
  unidadesEmpaque: UnidadEmpaqueItem[]
  recibidas: UnidadesEmpaqueRecibidas
  enabled: UnidadesEmpaqueEnabled
  onToggleEnabled: (tipo: TipoUnidadEmpaque, checked: boolean) => void
  onChangeRecibida: (tipo: TipoUnidadEmpaque, cantidad: number) => void
  onConfirm: () => void
  disabled?: boolean
  isSubmitting?: boolean
  canConfirm?: boolean
  confirmLabel?: string
}

function getMatchLabel(
  esperada: number,
  recibida: number,
  isEnabled: boolean
): { label: string; className: string } {
  if (!isEnabled) {
    return { label: 'Sin registrar', className: 'bg-gray-100 text-gray-600' }
  }
  if (esperada === 0 && recibida === 0) {
    return { label: 'No aplica', className: 'bg-gray-100 text-gray-600' }
  }
  if (recibida === esperada) {
    return { label: 'Coincide', className: 'bg-green-100 text-green-800' }
  }
  if (recibida === 0) {
    return { label: 'Sin cantidad', className: 'bg-red-100 text-red-800' }
  }
  if (recibida < esperada) {
    return { label: 'Parcial', className: 'bg-amber-100 text-amber-800' }
  }
  return { label: 'Excede', className: 'bg-blue-100 text-blue-800' }
}

const DevolucionEmpaqueTable: React.FC<DevolucionEmpaqueTableProps> = ({
  unidadesEmpaque,
  recibidas,
  enabled,
  onToggleEnabled,
  onChangeRecibida,
  onConfirm,
  disabled = false,
  isSubmitting = false,
  canConfirm = true,
  confirmLabel = 'Devolución para transferir'
}) => {
  const rows = buildEmpaqueVerificationRows(unidadesEmpaque)

  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-blue-800">
          Verificación de unidades de empaque
        </h3>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-center font-medium text-gray-700">
                Llegó
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">Tipo</th>
              <th className="px-3 py-2 text-center font-medium text-gray-700">
                Cantidad empacada
              </th>
              <th className="px-3 py-2 text-center font-medium text-gray-700">
                Cantidad recibida
              </th>
              <th className="px-3 py-2 text-center font-medium text-gray-700">
                Estado
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {rows.map((row) => {
              const isEnabled = Boolean(enabled[row.tipo])
              const recibida = Math.max(0, Number(recibidas[row.tipo]) || 0)
              const match = getMatchLabel(row.esperada, recibida, isEnabled)
              const inputDisabled = disabled || isSubmitting || !isEnabled

              return (
                <tr key={row.tipo} className={isEnabled ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-3 py-3 text-center">
                    <div className="flex justify-center">
                      <Switch
                        checked={isEnabled}
                        onChange={(checked) => onToggleEnabled(row.tipo, checked)}
                        disabled={disabled || isSubmitting}
                      />
                    </div>
                  </td>
                  <td className="px-3 py-2 font-medium text-gray-900">
                    {getEmpaqueLabel(row.tipo)}
                  </td>
                  <td className="px-3 py-2 text-center text-gray-700">{row.esperada}</td>
                  <td className="px-3 py-2">
                    <div className="mx-auto w-24">
                      <Input
                        type="number"
                        min={1}
                        value={isEnabled ? String(recibida || '') : ''}
                        placeholder={isEnabled ? '0' : '—'}
                        onChange={(event) => {
                          const parsed = Number.parseInt(event.target.value, 10)
                          onChangeRecibida(
                            row.tipo,
                            Number.isNaN(parsed) ? 0 : Math.max(0, parsed)
                          )
                        }}
                        disabled={inputDisabled}
                        aria-label={`Cantidad recibida de ${row.label}`}
                      />
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span
                      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${match.className}`}
                    >
                      {match.label}
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-col items-end gap-2">
        {!canConfirm && !disabled && (
          <p className="text-sm text-amber-700" role="status">
            Activa al menos una unidad y coloca la cantidad recibida (≥ 1).
          </p>
        )}
        <Button
          type="button"
          variant="primary"
          onClick={onConfirm}
          disabled={!canConfirm || disabled || isSubmitting}
          isLoading={isSubmitting}
        >
          {confirmLabel}
        </Button>
      </div>
    </div>
  )
}

export default DevolucionEmpaqueTable
